import { h } from '@omni-torn/ui';
import { server } from '../session';
import { ago, box, ext, loading, problem, profileUrl, table } from '../ui';

/**
 * Faction → Discord (public Discord bot, Phase C2/C4): for the faction's leader, co-leader and 'Discord servers' delegates — every Discord server that
 * gets the faction's data (alerts, balances, member lists): its name, who tied it (Torn name and id — never a key) and when,
 * with **Remove**; which one is the faction's home server (giveaways started in Torn post there — the first tied unless a
 * leader picks one). Who else may (the 'Discord servers' permission) is chosen on Faction → Delegates.
 * Data: GET/POST /api/faction/discord-servers (the bot keeps the ties). Other members see a short note.
 */

interface ServerRow {
  id: string;
  name: string | null;
  /** Null once whoever tied it deleted their Omni-Torn data. */
  tiedBy: { tornId: number; name: string | null } | null;
  at: number;
  home: boolean;
}
interface Answer {
  /** false: not the leader, co-leader or a 'Discord servers' delegate. */
  allowed?: boolean;
  servers: ServerRow[];
  homePicked?: boolean;
}

let built: HTMLElement | null = null;
/** A new visit to the Faction page reads it afresh. */
export function resetDiscord(): void {
  built = null;
}

export function discordSection(): HTMLElement {
  if (built) return built;
  const holder = h('div', { 'data-card': 'discord-servers' }, loading('Asking the Discord bot…'));
  const status = h('p', { class: 'save-status small', 'data-discord': 'status' });
  const act = (body: Record<string, unknown>, done: string) =>
    server<Answer>('POST', '/api/faction/discord-servers', body)
      .then((a) => {
        draw(a);
        status.className = 'save-status small ok';
        status.textContent = done;
      })
      .catch((err: Error) => {
        status.className = 'save-status small bad';
        status.textContent = err.message;
      });
  const draw = (a: Answer) => {
    if (a.allowed === false) {
      holder.replaceChildren(h('p', { class: 'notice', 'data-discord': 'leaders' }, 'Which Discord servers get your faction’s data is shown to your faction’s leader and co-leader, and to members they allowed (Faction → Delegates).'));
      return;
    }
    holder.replaceChildren(
      box('Discord servers that get our data', [
        h('p', { class: 'muted small' }, 'Each of these servers gets your faction’s alerts, balances and member lists through the Omni-Torn bot. Remove one and its data stops there at once (its log channel is told); its leaders can tie it again with /omni faction.'),
        table(
          a.servers,
          [
            { label: 'Server', get: (r) => h('span', { 'data-discord': 'server' }, r.name ?? `Server ${r.id}`, r.home ? h('span', { class: 'muted small', title: 'Giveaways started in Torn post here', 'data-discord': 'home-mark' }, ' 🏠 home server') : null), sort: (r) => (r.name ?? '').toLowerCase() },
            { label: 'Tied by', get: (r) => (r.tiedBy ? h('span', {}, ext(profileUrl(r.tiedBy.tornId), r.tiedBy.name ?? `#${r.tiedBy.tornId}`), h('span', { class: 'muted small' }, ` [${r.tiedBy.tornId}]`)) : h('span', { class: 'muted' }, '—')), sort: (r) => r.tiedBy?.tornId ?? 0 },
            { label: 'When', get: (r) => (r.at ? ago(r.at) : '—'), sort: (r) => -r.at },
            {
              label: '',
              get: (r) =>
                h(
                  'span',
                  {},
                  r.home ? null : h('button', { type: 'button', class: 'btn-small', 'data-discord': 'home', onclick: () => void act({ action: 'home', guildId: r.id }, `${r.name ?? 'That server'} is your home server now.`) }, 'Make home'),
                  ' ',
                  h('button', { type: 'button', class: 'btn-small btn-danger', 'data-discord': 'remove', onclick: () => confirm(`Stop sending your faction’s data to ${r.name ?? 'this server'}?`) && void act({ action: 'remove', guildId: r.id }, `${r.name ?? 'That server'} no longer gets your faction’s data.`) }, 'Remove'),
                ),
            },
          ],
          { empty: 'No Discord server gets your faction’s data. Add the Omni-Torn bot to your server (the /bot page) and run /omni faction there.', desc: false },
        ),
        a.homePicked ? h('p', { class: 'muted small' }, 'Home server picked by a leader. ', h('button', { type: 'button', class: 'btn-small', onclick: () => void act({ action: 'home', guildId: null }, 'Home server: the first one tied again.') }, 'Back to the first one tied')) : h('p', { class: 'muted small' }, 'Home server: the first one tied (giveaways started in Torn post there), unless you pick one.'),
      ]),
      h('p', { class: 'muted small', 'data-discord': 'who' }, 'Your faction’s leader and co-leader may tie and remove servers; others only if a leader allows them (Faction → Delegates: Discord servers) — a key with the faction’s API access alone doesn’t.'),
      status,
    );
  };
  void server<Answer>('GET', '/api/faction/discord-servers')
    .then(draw)
    .catch((err) => holder.replaceChildren(problem(err)));
  built = holder;
  return holder;
}
